import { useState, useEffect } from 'react';
import ICAL from 'ical.js';
import { apiFetch } from '../api';

function AirbnbEarnings({ bookingData, refreshKey }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReservations() {
      try {
        const res = await apiFetch('/api/calendar/airbnb');
        const text = await res.text();
        const parsed = ICAL.parse(text);
        const comp = new ICAL.Component(parsed);
        const vevents = comp.getAllSubcomponents('vevent');

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const upcoming = vevents
          .map((ve) => {
            const event = new ICAL.Event(ve);
            const summary = event.summary || '';
            return {
              id: event.uid || '',
              summary,
              checkIn: event.startDate.toJSDate(),
              checkOut: event.endDate.toJSDate(),
              nights: Math.round(
                (event.endDate.toJSDate() - event.startDate.toJSDate()) /
                  (1000 * 60 * 60 * 24)
              ),
            };
          })
          .filter((r) => r.checkOut >= now && r.summary !== 'Airbnb (Not available)')
          .sort((a, b) => a.checkIn - b.checkIn);

        setReservations(upcoming);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchReservations();
  }, [refreshKey]);

  if (loading) {
    return (
      <section className="card">
        <h2>Airbnb Earnings</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  function getAmount(id) {
    return Number(bookingData[id]?.amount || 0);
  }

  function daysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  // Calculate nights per month for a reservation
  function getNightsPerMonth(res) {
    const months = {};
    const d = new Date(res.checkIn);
    const end = new Date(res.checkOut);

    while (d < end) {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const label = d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'long', year: 'numeric' });
      if (!months[key]) months[key] = { label, year: d.getFullYear(), month: d.getMonth(), nights: 0 };
      months[key].nights += 1;
      d.setDate(d.getDate() + 1);
    }

    return months;
  }

  // Build monthly breakdown with prorated amounts and occupancy
  const byMonth = {};

  reservations.forEach((r) => {
    const amount = getAmount(r.id);
    const perNight = r.nights > 0 ? amount / r.nights : 0;
    const monthNights = getNightsPerMonth(r);

    Object.values(monthNights).forEach(({ label, year, month, nights }) => {
      if (!byMonth[label]) {
        byMonth[label] = {
          label,
          year,
          month,
          amount: 0,
          bookedNights: 0,
          totalDays: daysInMonth(year, month),
          bookingCount: 0,
        };
      }
      byMonth[label].amount += perNight * nights;
      byMonth[label].bookedNights += nights;
    });

    // Count bookings in the check-in month
    const checkInLabel = r.checkIn.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'long', year: 'numeric' });
    if (byMonth[checkInLabel]) byMonth[checkInLabel].bookingCount += 1;
  });

  // Sort by date
  const sortedMonths = Object.values(byMonth).sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  });

  const withAmounts = reservations.filter((r) => getAmount(r.id) > 0);
  const withoutAmounts = reservations.filter((r) => getAmount(r.id) <= 0);
  const total = withAmounts.reduce((sum, r) => sum + getAmount(r.id), 0);
  const totalNights = reservations.reduce((sum, r) => sum + r.nights, 0);

  return (
    <section className="card earnings-section">
      <h2>Airbnb Earnings</h2>
      <div className="earnings-total">
        <span className="earnings-amount">${total.toLocaleString()}</span>
        <span className="earnings-label">
          total from {reservations.length} reservation{reservations.length !== 1 ? 's' : ''} ({totalNights} nights)
          {totalNights > 0 && total > 0 && <> &middot; ADR ${Math.round(total / totalNights).toLocaleString()}</>}
        </span>
      </div>
      {withoutAmounts.length > 0 && (
        <div className="earnings-missing">
          {withoutAmounts.length} reservation{withoutAmounts.length !== 1 ? 's' : ''} without amounts set
        </div>
      )}
      <div className="earnings-breakdown">
        {sortedMonths.map((data) => {
          const occupancy = Math.round((data.bookedNights / data.totalDays) * 100);
          return (
            <div key={data.label} className="earnings-month">
              <span className="earnings-month-name">{data.label}</span>
              <span className="earnings-month-detail">
                {data.bookedNights} / {data.totalDays} nights &middot; {occupancy}% occ.
                {data.bookedNights > 0 && data.amount > 0 && <> &middot; ADR ${Math.round(data.amount / data.bookedNights)}</>}
              </span>
              <span className="earnings-month-amount">
                {data.amount > 0 ? `$${Math.round(data.amount).toLocaleString()}` : '--'}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default AirbnbEarnings;
